import { useState, useCallback } from "react";
import { Spinner } from "@chakra-ui/react";
import { FiEye, FiFileText } from "react-icons/fi";
import { cn } from "utils/helpers";
import { useNavigationPaths } from "newui/features/Auth/hooks/useNavigationPaths";
import { generatePdf } from "components/RFQ/PdfDownloadButton/api-PdfDownload";
import { CompanyLogoDisplay } from "newui/features/Company/components/CompanyLogo/CompanyLogoDisplay";
import { ScoreCell } from "./ScoreCell";
import type { MeetingCriteriaScore } from "../../types/meeting.type";
import Tooltip from "components/Tooltip";

interface ScoreComparisonTableProps {
  data: MeetingCriteriaScore;
  rfxId: number;
  onActiveScoreChange?: (score: number | null, element: HTMLElement | null) => void;
}

export const ScoreComparisonTable: React.FC<ScoreComparisonTableProps> = ({
  data,
  rfxId,
  onActiveScoreChange,
}) => {
  const { criterion, suppliers } = data;
  const { clientPaths } = useNavigationPaths();
  const [pdfLoadingId, setPdfLoadingId] = useState<number | null>(null);

  const panelMembers =
    suppliers.length > 0 ? suppliers[0].panel_member_scores : [];

  // Track hovered cell for crosshair highlighting
  const [hoveredCell, setHoveredCell] = useState<{
    col: number;
    row: number;
  } | null>(null);

  const handlePdfDownload = useCallback((inviteId: number) => {
    setPdfLoadingId(inviteId);
    generatePdf("responses", rfxId, inviteId, false).finally(() => {
      setPdfLoadingId(null);
    });
  }, [rfxId]);

  const handleCellEnter = useCallback(
    (e: React.MouseEvent, rowIdx: number, colIdx: number, score: number | null) => {
      setHoveredCell({ row: rowIdx, col: colIdx });
      onActiveScoreChange?.(score, e.currentTarget as HTMLElement);
    },
    [onActiveScoreChange]
  );

  const handleTableLeave = useCallback(() => {
    setHoveredCell(null);
    onActiveScoreChange?.(null, null);
  }, [onActiveScoreChange]);

  return (
    <div className="overflow-x-auto" onMouseLeave={handleTableLeave} data-testid="score-comparison-table">
      <div className="mb-3 px-4 pt-4">
        <h3 className="text-sm font-semibold text-gray-900">
          {criterion.name}
          <span className="ml-2 text-xs font-normal text-gray-500">
            Weight: {criterion.weight}%
          </span>
        </h3>
      </div>
      <table className="w-full text-sm border-collapse">
        <thead>
          <tr className="bg-gray-50 border-b border-gray-200">
            <th className="text-left px-4 py-2 font-medium text-gray-700 min-w-[160px]">
              Supplier
            </th>
            {panelMembers.map((member, colIdx) => (
              <th
                key={member.member_id}
                className={cn(
                  "text-center px-3 py-2 font-medium text-gray-700 min-w-[100px] transition-colors",
                  hoveredCell?.col === colIdx && "bg-blue-50"
                )}
              >
                <div>{member.member_name ?? `Member ${member.member_id}`}</div>
                <div className="text-xs font-normal text-gray-500">{member.role}</div>
              </th>
            ))}
            <th
              className={cn(
                "text-center px-3 py-2 font-medium text-gray-700 min-w-[90px] transition-colors",
                hoveredCell?.col === panelMembers.length && "bg-blue-50"
              )}
            >
              Avg Score
            </th>
            <th
              className={cn(
                "text-center px-3 py-2 font-medium text-gray-700 min-w-[90px] transition-colors",
                hoveredCell?.col === panelMembers.length + 1 && "bg-blue-50"
              )}
            >
              Wtd Avg
            </th>
          </tr>
        </thead>
        <tbody>
          {suppliers.map((supplier, rowIdx) => {
            const isRowHovered = hoveredCell?.row === rowIdx;

            return (
              <tr
                key={supplier.invite_id}
                className={cn(
                  "border-b border-gray-100 transition-colors",
                  supplier.stats.has_disparity && "border-l-4 border-l-amber-400",
                  isRowHovered
                    ? "bg-blue-50"
                    : supplier.stats.has_disparity
                      ? "bg-amber-50"
                      : "hover:bg-gray-50"
                )}
              >
                <td
                  className={cn(
                    "px-4 py-3 font-medium text-gray-900 transition-colors",
                    isRowHovered && "bg-blue-50"
                  )}
                  onMouseEnter={(e) => handleCellEnter(e, rowIdx, -1, null)}
                >
                  <div className="flex items-center gap-2">
                    <div className="flex items-center gap-1.5 shrink-0">
                      <Tooltip label="View submission" shouldWrapChildren={false}>
                        <a
                          href={clientPaths.viewRfxSubmission(rfxId, supplier.invite_id)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-gray-400 hover:text-blue-600 transition-colors"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <FiEye className="w-3.5 h-3.5" />
                        </a>
                      </Tooltip>
                      <Tooltip label="Download PDF" shouldWrapChildren={false}>
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); handlePdfDownload(supplier.invite_id); }}
                          disabled={pdfLoadingId === supplier.invite_id}
                          className="text-gray-400 hover:text-blue-600 transition-colors disabled:opacity-50"
                        >
                          {pdfLoadingId === supplier.invite_id ? <Spinner size="xs" /> : <FiFileText className="w-3.5 h-3.5" />}
                        </button>
                      </Tooltip>
                    </div>
                    <CompanyLogoDisplay
                      size="md"
                      supplierLogoUrl={supplier.supplier_logo}
                      supplierName={supplier.supplier_name}
                    />
                    <div className="min-w-0">
                      <div className="truncate">{supplier.supplier_name}</div>
                      {supplier.stats.has_disparity && (
                        <div className="text-xs text-amber-600 font-normal mt-0.5">
                          Score disparity ({supplier.stats.min}&ndash;{supplier.stats.max})
                        </div>
                      )}
                    </div>
                  </div>
                </td>
                {
                  supplier.panel_member_scores.map((score, colIdx) => {
                    const isCellHovered =
                      isRowHovered && hoveredCell?.col === colIdx;
                    const isColHovered = hoveredCell?.col === colIdx;

                    return (
                      <td
                        key={score.member_id}
                        className={cn(
                          "px-3 py-3 align-top transition-colors",
                          isCellHovered
                            ? "bg-blue-100 ring-2 ring-inset ring-blue-400 rounded"
                            : isRowHovered || isColHovered
                              ? "bg-blue-50"
                              : ""
                        )}
                        onMouseEnter={(e) =>
                          handleCellEnter(e, rowIdx, colIdx, score.final_score)
                        }
                      >
                        <ScoreCell score={score} />
                      </td>
                    );
                  })
                }
                <td
                  className={cn(
                    "px-3 py-3 text-center font-medium transition-colors",
                    isRowHovered && hoveredCell?.col === panelMembers.length
                      ? "bg-blue-100"
                      : isRowHovered || hoveredCell?.col === panelMembers.length
                        ? "bg-blue-50"
                        : ""
                  )}
                  onMouseEnter={(e) =>
                    handleCellEnter(e, rowIdx, panelMembers.length, supplier.stats.average)
                  }
                >
                  {supplier.stats.average !== null ? (
                    supplier.stats.average.toFixed(1)
                  ) : (
                    <span className="text-gray-400">&mdash;</span>
                  )}
                </td>
                <td
                  className={cn(
                    "px-3 py-3 text-center text-gray-600 transition-colors",
                    isRowHovered &&
                      hoveredCell?.col === panelMembers.length + 1
                      ? "bg-blue-100"
                      : isRowHovered ||
                        hoveredCell?.col === panelMembers.length + 1
                        ? "bg-blue-50"
                        : ""
                  )}
                  onMouseEnter={(e) =>
                    handleCellEnter(
                      e,
                      rowIdx,
                      panelMembers.length + 1,
                      supplier.stats.weighted_average
                    )
                  }
                >
                  {supplier.stats.weighted_average !== null ? (
                    supplier.stats.weighted_average.toFixed(1)
                  ) : (
                    <span className="text-gray-400">&mdash;</span>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div >
  );
};
